import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ContentArea from '../components/content-area/Content';
import LocalText from '../components/common/LocalText';
import PackageModal from '../components/items/PackageModal';
import StatusTabs from '../components/approval/StatusTabs';
import ApprovalStatusBadge from '../components/approval/ApprovalStatusBadge';
import SubmitForApprovalButton from '../components/approval/SubmitForApprovalButton';
import WithdrawButton from '../components/approval/WithdrawButton';
import { useSpaceId } from '../hooks/useSpaceId';
import { usePermissions } from '../hooks/usePermissions';
import { useToast } from '../context/ToastContext';
import { useCurrency } from '../context/CurrencyContext';
import { buildVersionedList, filterVersionedList, countVersionedTabs } from '../utils/versionedContent';
import { getPackages, createPackage, updatePackage, deletePackage } from '../api/packageApi';
import { getTaxCategories, createTaxClass } from '../api/itemApi';

export default function Packages() {
  const spaceId = useSpaceId();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currencies } = useCurrency();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/items', 'package-edit');

  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [taxCategories, setTaxCategories] = useState([]);
  const [statusTab, setStatusTab] = useState('live');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalReadOnly, setModalReadOnly] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    if (!spaceId) return;
    setLoading(true);
    getPackages()
      .then((data) => setPackages(Array.isArray(data) ? data : []))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, [spaceId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!spaceId) return;
    getTaxCategories().then((data) => setTaxCategories(Array.isArray(data) ? data : [])).catch(() => setTaxCategories([]));
  }, [spaceId]);

  const handleAddTaxClass = async (newClass) => {
    const created = await createTaxClass(newClass);
    setTaxCategories((prev) => [...prev, created]);
    return created;
  };

  const packageVersioned = useMemo(() => buildVersionedList(packages, 'packageId'), [packages]);
  const counts = useMemo(() => countVersionedTabs(packages, 'packageId'), [packages]);
  const filtered = useMemo(() => filterVersionedList(packageVersioned, statusTab), [packageVersioned, statusTab]);

  const handleCreate = () => { setEditing(null); setModalReadOnly(false); setModalOpen(true); };
  const handleEdit = (pkg, readOnly = false) => { setEditing(pkg); setModalReadOnly(readOnly); setModalOpen(true); };

  const handleSave = async (payload) => {
    try {
      if (editing) {
        const result = await updatePackage(editing.packageId, payload);
        toast.success(result?.message || 'Package updated.');
      } else {
        await createPackage(payload);
        toast.success('Package created.');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.errorMessage || 'Failed to save package.');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const result = await deletePackage(deleteTarget.packageId);
      toast.success(result?.message || 'Package deleted.');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delete package.');
    }
  };

  // Same tax-inclusive total ItemCard.jsx shows (customer's actual price), not the raw base cost —
  // a package has no per-item discounts, so there's no discount term to fold in here.
  const formatPrice = (pkg) => {
    const price = pkg.prices?.[0];
    const currency = currencies.find((c) => c.id === price?.currencyId);
    const decimalPlaces = currency?.decimalPlaces ?? 2;
    const baseCost = (price?.baseCost ?? 0) / Math.pow(10, decimalPlaces);
    const rate = pkg.tax?.ratePct ?? 0;
    const subtotal = pkg.tax?.isInclusive ? baseCost : baseCost + baseCost * rate / 100;
    return `${currency?.symbol ?? ''}${subtotal.toFixed(decimalPlaces)}`;
  };

  return (
    <ContentArea title="Packages" icon={<Inventory2OutlinedIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <StatusTabs items={packages} counts={counts} value={statusTab} onChange={setStatusTab} statusKey="approvalStatus" />
        {canEdit && (
          <Button
            variant="contained" startIcon={<AddIcon />} onClick={handleCreate}
            sx={{ background: '#0a326d', color: 'white', fontWeight: 700, fontSize: 12, textTransform: 'none', borderRadius: '10px', '&:hover': { background: '#0d3d85' } }}
          >
            <LocalText>New Package</LocalText>
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid rgba(226,232,240,0.8)', p: 4, textAlign: 'center' }}>
          <Typography fontSize={12} sx={{ color: '#94a3b8' }}><LocalText>No packages yet.</LocalText></Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 2 }}>
          {filtered.map((pkg) => {
            const isSnapshot    = pkg._viewType === 'snapshot';
            const isLocked      = pkg.approvalStatus === 'pending' || pkg.approvalStatus === 'rejected';
            const canDirectEdit = pkg.contentStatus === 'draft' && !isLocked;
            const pkgName       = pkg.nameLocalizations?.values?.[0]?.value ?? '—';
            return (
              <Box key={isSnapshot ? `snapshot-${pkg.packageId}` : pkg.packageId} sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.07)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ height: 120, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                  {pkg.thumbnail?.linkLocalizations?.[0]?.mediaLink && (
                    <Box component="img" src={pkg.thumbnail.linkLocalizations[0].mediaLink} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                </Box>
                <Box sx={{ p: 1.75, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography fontWeight={700} fontSize={13}>{pkgName}</Typography>
                  <Typography fontWeight={800} fontSize={16} sx={{ color: '#1e3a5f' }}>{formatPrice(pkg)}</Typography>
                  <Typography fontSize={11} sx={{ color: '#94a3b8' }}>{pkg.memberCount ?? 0} item(s)</Typography>
                  {/* Live cards show contentStatus; snapshot (pending/rejected proposal) cards show approvalStatus — same rule as Items/Discounts */}
                  {(isSnapshot ? pkg.approvalStatus : pkg.contentStatus) && (
                    <ApprovalStatusBadge status={isSnapshot ? pkg.approvalStatus : pkg.contentStatus} actionType={pkg.approvalActionType} />
                  )}
                </Box>
                <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {!isSnapshot && canEdit && (
                      <IconButton size="small" onClick={() => handleEdit(pkg, isLocked)} title={isLocked ? 'View (read-only)' : 'Edit'}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {!isSnapshot && canEdit && (
                      <IconButton size="small" disabled={isLocked} onClick={() => setDeleteTarget(pkg)} title="Delete" sx={{ color: '#ef4444' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                    {!isSnapshot && canEdit && canDirectEdit && (
                      <SubmitForApprovalButton contentType="package" contentId={pkg.packageId} contentName={pkgName} onSubmitted={load} />
                    )}
                    {isSnapshot && canEdit && pkg.approvalStatus === 'pending' && pkg.approvalId && (
                      <WithdrawButton approvalId={pkg.approvalId} onWithdrawn={load} />
                    )}
                    {isSnapshot && canEdit && pkg.approvalStatus === 'rejected' && pkg.approvalId && (
                      <WithdrawButton approvalId={pkg.approvalId} onWithdrawn={load} variant="dismiss" />
                    )}
                  </Box>
                  {!isSnapshot && (
                    <Button
                      size="small"
                      startIcon={<PlaylistAddIcon sx={{ fontSize: 15 }} />}
                      onClick={() => navigate(`/packages/${pkg.packageId}/items`)}
                      sx={{ color: '#0a326d', fontWeight: 700, fontSize: 11, textTransform: 'none', px: 1, minWidth: 0, '&:hover': { bgcolor: '#eff6ff' } }}
                    >
                      <LocalText>Add Item</LocalText>
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <PackageModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setModalReadOnly(false); }}
        onSave={handleSave}
        initialData={editing}
        taxCategories={taxCategories}
        onAddTaxClass={handleAddTaxClass}
        readOnly={modalReadOnly}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15 }}>Delete Package?</DialogTitle>
        <DialogContent>
          <Typography fontSize={13}>
            Are you sure you want to delete "{deleteTarget?.nameLocalizations?.values?.[0]?.value}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </ContentArea>
  );
}
